import React, { useLayoutEffect, useRef } from "react";
import { DynamicForm } from "./DynamicForm";
import { FORM_SCHEMA } from "./formSchema";

function valuesEqual(left, right) {
  if (left === right) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return String(left ?? "") === String(right ?? "");
  }
}

function focusEpisodeId(fieldId) {
  const suffix = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `focus:${fieldId}:${suffix}`;
}

function valueFingerprint(value) {
  let text;
  try {
    text = JSON.stringify(value);
  } catch {
    text = String(value ?? "");
  }
  if (typeof text !== "string") text = String(value ?? "");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

/** The shared end-stage review and edit surface used by every condition. */
export function FinalReview({
  record,
  setRecord,
  onSubmit,
  onTelemetry,
  onFieldCommit,
  title = "Review the completed report",
  instructions = "Compare every AI-filled value with the scenario. Edit any report value you want to change, then submit.",
  submitLabel = "Approve & submit report",
  telemetrySurface = "final_review",
  submitting = false,
  submitError = "",
  fieldAnnotations = {},
}) {
  const focusEpisodesRef = useRef(new Map());

  useLayoutEffect(() => {
    if (onTelemetry) {
      onTelemetry({
        event_type: "final_review_opened",
        surface: telemetrySurface,
      });
    }
  }, [onTelemetry, telemetrySurface]);

  const fieldFocus = (fieldId, value) => {
    if (focusEpisodesRef.current.has(fieldId)) return;
    const episode = {
      focus_episode_id: focusEpisodeId(fieldId),
      initial_value: value,
      latest_value: value,
      attempted: false,
    };
    focusEpisodesRef.current.set(fieldId, episode);
    if (onTelemetry) {
      onTelemetry({
        event_type: "field_focus_start",
        field_id: fieldId,
        surface: telemetrySurface,
        focus_episode_id: episode.focus_episode_id,
      });
    }
  };

  const fieldInput = (fieldId, renderedValue, nextValue) => {
    if (!focusEpisodesRef.current.has(fieldId)) fieldFocus(fieldId, renderedValue);
    const episode = focusEpisodesRef.current.get(fieldId);
    episode.latest_value = nextValue;
    if (!episode.attempted && !valuesEqual(episode.initial_value, nextValue)) {
      episode.attempted = true;
      if (onTelemetry) {
        onTelemetry({
          event_type: "field_edit_attempt_start",
          field_id: fieldId,
          surface: telemetrySurface,
          focus_episode_id: episode.focus_episode_id,
          initial_value_hash: valueFingerprint(episode.initial_value),
          value_hash_algorithm: "fnv1a32",
        });
      }
    }
  };

  const fieldBlur = (fieldId, value) => {
    if (!focusEpisodesRef.current.has(fieldId)) return;
    const episode = focusEpisodesRef.current.get(fieldId);
    focusEpisodesRef.current.delete(fieldId);
    const finalValue = episode.attempted ? episode.latest_value : value;
    if (onTelemetry) {
      onTelemetry({
        event_type: "field_focus_end",
        field_id: fieldId,
        surface: telemetrySurface,
        focus_episode_id: episode.focus_episode_id,
      });
    }
    if (episode.attempted && valuesEqual(episode.initial_value, finalValue)) {
      if (onTelemetry) {
        onTelemetry({
          event_type: "field_edit_reverted",
          field_id: fieldId,
          surface: telemetrySurface,
          focus_episode_id: episode.focus_episode_id,
        });
      }
    } else if (episode.attempted) {
      const change = {
        event_type: "final_field_edit_committed",
        field_id: fieldId,
        surface: telemetrySurface,
        focus_episode_id: episode.focus_episode_id,
        original_value: episode.initial_value,
        value: finalValue,
      };
      if (onTelemetry) onTelemetry(change);
      if (onFieldCommit) onFieldCommit(change);
    }
  };

  return (
    <div className="chat-review">
      <p className="cr-title">{title}</p>
      <p className="cr-sub">{instructions}</p>
      <DynamicForm
        schema={FORM_SCHEMA}
        value={record}
        onChange={setRecord}
        telemetrySurface={telemetrySurface}
        onTelemetry={onTelemetry}
        onFieldFocus={fieldFocus}
        onFieldInput={fieldInput}
        onFieldBlur={fieldBlur}
        fieldAnnotations={fieldAnnotations}
      />
      <div className="cr-foot">
        {submitError && <p className="review-submit-note error" role="alert">{submitError}</p>}
        <button className="btn ok" type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? "Submitting…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

export default FinalReview;
